/**
 * Identity of a print's CONTENT, for the 5-second duplicate guard in
 * `PrintUtils.sendToPrinter`.
 *
 * The guard exists to swallow a double-tap on Print — the same picture, twice,
 * within a blink. Keying it therefore has one hard requirement: two different
 * pictures must never collide.
 *
 * `imageUrl.substring(0, 100)` did collide, always. What arrives at the guard
 * is a `data:image/jpeg;base64,…` URL, and the first 100 characters of one are
 * the JFIF header plus the start of the quantisation table — bytes fixed by the
 * encoder and the quality setting, identical for every image the booth exports.
 * Two unrelated karcis do not diverge until roughly character 855. So a flow
 * that legitimately prints several different pictures in a row had its 2nd and
 * 3rd jobs rejected as duplicates (Cikstik, 3-ticket sessions: ticket 1 out,
 * tickets 2-3 refused with "Print job sent too recently", operator shown a
 * print-failure toast). It looked intermittent only because a job slower than
 * 5s let the window lapse and the next ticket through.
 *
 * Hashing the whole string is what makes the guard mean what it says, and it is
 * affordable: FNV-1a over an 8MB data URL is ~20ms against a compose-and-send
 * measured in seconds. Length is mixed in so two strings would have to collide
 * on both to be confused.
 *
 * Kept dependency-free and in its own module so it stays directly testable —
 * importing `printUtils` pulls the renderer's whole media stack.
 */
export function contentFingerprint(imageUrl: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < imageUrl.length; i++) {
    hash ^= imageUrl.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `${imageUrl.length.toString(36)}-${hash.toString(36)}`
}
